import { z } from 'zod';
import { clearVoiceSocketHandlers, consumeVoiceTicket } from './socket';
import { ElowenApiError } from '../../lib/elowenClient';
import { VOICE_AUDIO_FRAME_MAX_BYTES, VOICE_CALL_RECORD_BOUNDS, VOICE_DICTATION_STOP_BUDGET, VOICE_SAMPLE_RATE } from '../../lib/voice';
import type { VoiceDictationControl, VoiceDictationResponse } from '../../../src/shared/wireContract';

/** Why a dictation produced no (more) text; each code has a translated toast. */
export type DictationFailure = 'mic_denied' | 'mic_failed' | 'not_granted' | 'disabled' | 'spend_limit' | 'provider_error' | 'dictation_active' | 'network';
export type DictationState = 'idle' | 'connecting' | 'listening' | 'finishing';

/** Speech ends a segment after this much silence; the daemon then finishes that segment's text. */
const DICTATION_SEGMENT_SILENCE_MS = 700;
/** Audio captured while the session is still opening is kept up to this much, so the first words survive. */
const PRE_ROLL_MS = 3000;
/** A frame is speech when its RMS level (0..1) is above the quiet-room floor and clearly above the noise seen. */
const SPEECH_LEVEL_MIN = 0.012;
const SPEECH_OVER_NOISE = 3;
/** Less speech than this in a segment is a click or a breath and is not worth finishing as text. */
const SEGMENT_SPEECH_MIN_MS = 100;
const SOCKET_OPEN_MS = 15_000;

const endedFrame = z.object({ type: z.literal('ended'), reason: z.enum(['hangup', 'max_duration', 'spend_limit', 'disconnected', 'silence', 'provider_error', 'daemon_restart']) });
const frame = z.union([
  z.object({ type: z.literal('delta'), itemId: z.string().min(1).max(256), text: z.string().max(VOICE_CALL_RECORD_BOUNDS.maxEntryChars) }),
  z.object({ type: z.literal('final'), itemId: z.string().min(1).max(256), text: z.string().max(VOICE_CALL_RECORD_BOUNDS.maxEntryChars) }),
  endedFrame,
]);
const refusals = new Map<string, DictationFailure>([['dictation_active', 'dictation_active'], ['not_granted', 'not_granted'], ['unlinked', 'not_granted'], ['disabled', 'disabled'], ['voice_unavailable', 'disabled'], ['spend_limit', 'spend_limit']]);
const endFailures = new Map<string, DictationFailure>([['spend_limit', 'spend_limit'], ['provider_error', 'provider_error'], ['daemon_restart', 'provider_error'], ['disconnected', 'network'], ['silence', 'network']]);

export interface DictationDeps {
  capture(frame: (pcm: ArrayBuffer) => void, failed: () => void, signal: AbortSignal): Promise<{ stop(): void }>;
  start(): Promise<VoiceDictationResponse>;
  socket(dictationId: string, ticket: string): WebSocket;
  /** The pause that ends a dictation by itself, in seconds; null never does. Read at every frame. */
  pauseSeconds(): number | null;
}
export interface DictationListener {
  state(state: DictationState): void;
  delta(itemId: string, text: string): void;
  final(itemId: string, text: string): void;
  /** The dictation is over. `failure` is null when it ended as asked or by itself. */
  ended(failure: DictationFailure | null): void;
}

function startFailure(error: unknown): DictationFailure {
  if (error instanceof DOMException) return error.name === 'NotAllowedError' || error.name === 'SecurityError' ? 'mic_denied' : 'mic_failed';
  if (!(error instanceof ElowenApiError)) return 'network';
  return refusals.get(error.code ?? '') ?? (error.status >= 500 || error.status === 402 ? 'provider_error' : 'network');
}

/** One live dictation: microphone frames go to the daemon over the one-shot ticket socket, the daemon's
 *  interim and final text comes back. The browser decides where a segment ends (700 ms of silence after
 *  speech sends `commit`) and when the person has been quiet long enough to stop on their own. Both are
 *  measured in captured audio time, so they hold whatever the tab's timers do. */
export class DictationSession {
  private socket?: WebSocket;
  private microphone?: { stop(): void };
  private preparation?: AbortController;
  private queue: Array<ArrayBuffer | Extract<VoiceDictationControl, { type: 'commit' }>> = [];
  private queuedMs = 0;
  private generation = 0;
  private current: DictationState = 'idle';
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private settle?: () => void;
  // Voice activity, counted in audio milliseconds.
  private noise = 0.004;
  private silentMs = 0;
  private speechMs = 0;
  private segmentOpen = false;

  constructor(private deps: DictationDeps, private listener: DictationListener) {}

  get state(): DictationState { return this.current; }

  private set(state: DictationState): void { this.current = state; this.listener.state(state); }
  private timer(ms: number, action: () => void): ReturnType<typeof setTimeout> {
    const handle = setTimeout(() => { this.timers.delete(handle); action(); }, ms);
    this.timers.add(handle);
    return handle;
  }
  private send(message: VoiceDictationControl): void {
    if (this.socket?.readyState === 1) this.socket.send(JSON.stringify(message));
    else if (message.type === 'commit' && this.current === 'connecting') this.queue.push(message);
  }
  private release(): void {
    this.microphone?.stop(); this.microphone = undefined;
    this.preparation?.abort(); this.preparation = undefined;
  }
  private teardown(): void {
    this.release();
    for (const handle of this.timers) clearTimeout(handle);
    this.timers.clear();
    this.queue = []; this.queuedMs = 0;
    if (this.socket) {
      clearVoiceSocketHandlers(this.socket);
      this.socket.close(); this.socket = undefined;
    }
  }
  private end(failure: DictationFailure | null): void {
    if (this.current === 'idle') return;
    ++this.generation; this.teardown();
    this.set('idle');
    this.settle?.(); this.settle = undefined;
    this.listener.ended(failure);
  }

  /** Opens the microphone first, in the caller's click stack, then the daemon session and its socket. The
   *  daemon session is only requested once the microphone is ours, so a refused permission never holds the
   *  account's single dictation slot. */
  async start(): Promise<void> {
    if (this.current !== 'idle') return;
    const generation = ++this.generation;
    this.silentMs = 0; this.speechMs = 0; this.segmentOpen = false; this.noise = 0.004;
    this.preparation = new AbortController();
    this.set('connecting');
    try {
      const microphone = await this.deps.capture(pcm => { this.frame(generation, pcm); }, () => { if (generation === this.generation) this.end('mic_failed'); }, this.preparation.signal);
      if (generation !== this.generation) { microphone.stop(); return; }
      this.microphone = microphone;
      const { dictationId, ticket } = await this.deps.start();
      if (generation !== this.generation) { this.abandon(dictationId, ticket); return; }
      this.socket = this.deps.socket(dictationId, ticket);
      this.socket.binaryType = 'arraybuffer';
      const opening = this.timer(SOCKET_OPEN_MS, () => { this.end('network'); });
      this.socket.onopen = () => {
        clearTimeout(opening); this.timers.delete(opening);
        this.set('listening');
        for (const entry of this.queue.splice(0)) this.socket?.send(entry instanceof ArrayBuffer ? entry : JSON.stringify(entry));
        this.queuedMs = 0;
      };
      this.socket.onmessage = event => { this.message(generation, event.data); };
      this.socket.onclose = () => { if (generation === this.generation) this.end(this.current === 'finishing' ? null : 'network'); };
      this.socket.onerror = () => { if (generation === this.generation) this.end('network'); };
    } catch (error) {
      if (generation !== this.generation) return;
      this.end(startFailure(error));
    }
  }

  /** A session the daemon opened for an attempt that was ended meanwhile: attach once to spend its ticket,
   *  cancel it so the account's slot is free at once, and close. */
  private abandon(dictationId: string, ticket: string): void {
    consumeVoiceTicket(this.deps.socket, dictationId, ticket, { type: 'cancel' });
  }

  private message(generation: number, data: unknown): void {
    if (generation !== this.generation) return;
    try {
      if (typeof data !== 'string') throw new Error('Invalid dictation frame');
      const message = frame.parse(JSON.parse(data));
      if (message.type === 'delta') this.listener.delta(message.itemId, message.text);
      else if (message.type === 'final') this.listener.final(message.itemId, message.text);
      else this.end(endFailures.get(message.reason) ?? null);
    } catch { this.end('provider_error'); }
  }

  private frame(generation: number, pcm: ArrayBuffer): void {
    if (generation !== this.generation || this.current === 'finishing' || this.current === 'idle') return;
    const ms = pcm.byteLength / (VOICE_SAMPLE_RATE * 2) * 1000;
    if (pcm.byteLength === 0 || pcm.byteLength % 2 || pcm.byteLength > VOICE_AUDIO_FRAME_MAX_BYTES) { this.end('mic_failed'); return; }
    if (this.socket?.readyState === 1) this.socket.send(pcm);
    else {
      this.queue.push(pcm); this.queuedMs += ms;
      while (this.queuedMs > PRE_ROLL_MS) {
        const entry = this.queue.shift()!;
        if (entry instanceof ArrayBuffer) this.queuedMs -= entry.byteLength / (VOICE_SAMPLE_RATE * 2) * 1000;
      }
      // A boundary whose entire audio was trimmed must not commit an empty provider buffer.
      while (this.queue.length && !(this.queue[0] instanceof ArrayBuffer)) this.queue.shift();
    }
    this.listen(pcm, ms);
  }

  /** Voice activity for one frame: finishes a segment after a short silence and stops after the chosen pause. */
  private listen(pcm: ArrayBuffer, ms: number): void {
    const samples = new Int16Array(pcm);
    let sum = 0;
    for (const sample of samples) sum += sample * sample;
    const level = Math.sqrt(sum / samples.length) / 32768;
    if (level > Math.max(SPEECH_LEVEL_MIN, this.noise * SPEECH_OVER_NOISE)) {
      this.silentMs = 0; this.speechMs += ms;
      if (this.speechMs >= SEGMENT_SPEECH_MIN_MS) this.segmentOpen = true;
      return;
    }
    this.noise += (level - this.noise) * 0.05;
    this.silentMs += ms;
    if (this.segmentOpen && this.silentMs >= DICTATION_SEGMENT_SILENCE_MS) {
      this.send({ type: 'commit' }); this.segmentOpen = false; this.speechMs = 0;
    }
    const pause = this.deps.pauseSeconds();
    if (pause !== null && this.silentMs >= pause * 1000) void this.finish();
  }

  /** Stops listening, lets the daemon finish what was said and resolves once its final text is in, or
   *  after a bounded wait; the interim text stays either way. Resolves at once when nothing is running. */
  finish(): Promise<void> {
    if (this.current === 'idle') return Promise.resolve();
    if (this.current === 'finishing') return new Promise(resolve => { const previous = this.settle; this.settle = () => { previous?.(); resolve(); }; });
    if (this.socket?.readyState !== 1) { this.end(null); return Promise.resolve(); }
    if (this.segmentOpen) this.send({ type: 'commit' });
    this.send({ type: 'stop' });
    this.release();
    this.set('finishing');
    this.timer(VOICE_DICTATION_STOP_BUDGET.browserWaitMs, () => { this.end(null); });
    return new Promise(resolve => { this.settle = resolve; });
  }

  /** Ends at once; the daemon discards what it holds. */
  cancel(): void {
    if (this.current === 'idle') return;
    this.send({ type: 'cancel' });
    this.end(null);
  }

  dispose(): void {
    if (this.current === 'idle') return;
    this.send({ type: 'cancel' });
    ++this.generation; this.teardown(); this.current = 'idle';
    this.settle?.(); this.settle = undefined;
  }
}
